import { forwardRef, type ReactNode } from 'react'
import { StatusChip as DsStatusChip, type StatusChipProps as DsStatusChipProps } from '@scripsteam/scrips-signal-ds'

import { cn } from '@/lib/utils'

/**
 * BridgeStatusChip — adapter over the Signal DS chip. Every screen imports this one, never the DS chip
 * directly (same adapter in the practitioner app).
 *   - Always soft: the semantic tint behind the semantic text. `tone` is accepted for compatibility
 *     and ignored; the solid (filled) chip is retired.
 *   - One size: 20 tall (the DS chip is 16), 8px sides, 13px label. `size="xs"` is accepted and ignored.
 *   - Draft is hollow: a dashed grey outline and secondary ink, like a draft on the care plan board.
 *     A chip whose label is "Draft" (or "AI draft") is a draft on its own; `draft` forces it either way.
 *     The draft colours live in showcase-bridge.css under `[data-chip-look="draft"]`, because the DS
 *     chip paints its colours inline.
 */
export interface StatusChipProps extends DsStatusChipProps {
  draft?: boolean
}

const isDraftLabel = (children: ReactNode) =>
  typeof children === 'string' && /^(ai\s+)?draft$/i.test(children.trim())

export const BridgeStatusChip = forwardRef<HTMLSpanElement, StatusChipProps>(
  ({ draft, tone: _tone, size: _size, className, children, ...rest }, ref) => {
    const isDraft = draft ?? isDraftLabel(children)
    return (
      <DsStatusChip
        ref={ref}
        {...rest}
        tone="soft"
        data-chip-look={isDraft ? 'draft' : 'soft'}
        // Geometry last, so no caller (or a stretching flex row) can resize one chip.
        className={cn(className, 'h-5 shrink-0 self-center px-2 leading-5')}
      >
        {children}
      </DsStatusChip>
    )
  },
)
BridgeStatusChip.displayName = 'BridgeStatusChip'
